import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { apiClient } from '../../../framework/api/api.client';

interface FunctionMetric {
  functionName: string;
  callCount: number;
  totalExecutionMs: number;
  averageExecutionMs: number;
  minExecutionMs: number;
  maxExecutionMs: number;
  successCount: number;
  failureCount: number;
  successRate: number;
  lastCalledAt: Date;
}

export const PerformancePanel: React.FC = () => {
  const [topFunctions, setTopFunctions] = useState<FunctionMetric[]>([]);
  const [slowestFunctions, setSlowestFunctions] = useState<FunctionMetric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      loadMetrics();
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  const loadMetrics = async () => {
    try {
      const [topRes, slowestRes] = await Promise.all([
        apiClient.call('/ai-observatory/metrics/functions/top?limit=10'),
        apiClient.call('/ai-observatory/metrics/functions/slowest?limit=10'),
      ]);

      setTopFunctions(topRes.metrics || []);
      setSlowestFunctions(slowestRes.metrics || []);
    } catch (error) {
      console.error('Error loading function metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMs = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getSuccessRateColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 95) return 'success';
    if (rate >= 80) return 'warning';
    return 'error';
  };

  const getPerformanceColor = (avgMs: number): 'success' | 'warning' | 'error' => {
    if (avgMs < 100) return 'success';
    if (avgMs < 500) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Top Functions by Usage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon />
                  <Typography variant="h6">Most Used Functions</Typography>
                </Box>
              }
              action={
                <IconButton onClick={loadMetrics} size="small">
                  <RefreshIcon />
                </IconButton>
              }
            />
            <CardContent>
              {loading ? (
                <LinearProgress />
              ) : topFunctions.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No function calls recorded yet
                </Typography>
              ) : (
                <List>
                  {topFunctions.map((metric, index) => (
                    <ListItem
                      key={metric.functionName}
                      sx={{
                        mb: 1,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 'bold',
                                color: 'text.secondary',
                                minWidth: 24,
                              }}
                            >
                              #{index + 1}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                flex: 1,
                              }}
                            >
                              {metric.functionName}()
                            </Typography>
                            <Chip
                              label={`${metric.callCount} calls`}
                              color="primary"
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                            <Chip
                              icon={<TimerIcon />}
                              label={`Avg: ${formatMs(metric.averageExecutionMs)}`}
                              size="small"
                              variant="outlined"
                              color={getPerformanceColor(metric.averageExecutionMs)}
                            />
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={`${metric.successRate}% success`}
                              size="small"
                              variant="outlined"
                              color={getSuccessRateColor(metric.successRate)}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 2.5 }}>
                              Range: {formatMs(metric.minExecutionMs)} - {formatMs(metric.maxExecutionMs)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Slowest Functions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDownIcon />
                  <Typography variant="h6">Slowest Functions</Typography>
                </Box>
              }
              subheader="Performance optimization candidates (min 5 calls)"
            />
            <CardContent>
              {loading ? (
                <LinearProgress />
              ) : slowestFunctions.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No data available (need 5+ calls per function)
                </Typography>
              ) : (
                <List>
                  {slowestFunctions.map((metric, index) => (
                    <ListItem
                      key={metric.functionName}
                      sx={{
                        mb: 1,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        border: 1,
                        borderColor: getPerformanceColor(metric.averageExecutionMs) === 'error' 
                          ? 'error.main' 
                          : 'divider',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getPerformanceColor(metric.averageExecutionMs) === 'error' && (
                              <ErrorIcon color="error" />
                            )}
                            <Typography
                              variant="body1"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                flex: 1,
                              }}
                            >
                              {metric.functionName}()
                            </Typography>
                            <Chip
                              label={formatMs(metric.averageExecutionMs)}
                              color={getPerformanceColor(metric.averageExecutionMs)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption">
                                Min: {formatMs(metric.minExecutionMs)}
                              </Typography>
                              <Typography variant="caption">
                                Max: {formatMs(metric.maxExecutionMs)}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min((metric.averageExecutionMs / metric.maxExecutionMs) * 100, 100)}
                              color={getPerformanceColor(metric.averageExecutionMs)}
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {metric.callCount} calls • {metric.successRate}% success
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Summary */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Performance Summary" />
            <CardContent>
              {loading ? (
                <LinearProgress />
              ) : (
                <Grid container spacing={2}>
                  {/* Total Calls */}
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <SpeedIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {topFunctions.reduce((sum, m) => sum + m.callCount, 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        TOTAL FUNCTION CALLS
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Average Performance */}
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <TimerIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {topFunctions.length > 0
                          ? Math.round(
                              topFunctions.reduce((sum, m) => sum + m.averageExecutionMs, 0) /
                                topFunctions.length
                            )
                          : 0}
                        ms
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        AVG EXECUTION TIME
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Success Rate */}
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {topFunctions.length > 0
                          ? Math.round(
                              topFunctions.reduce((sum, m) => sum + m.successRate, 0) /
                                topFunctions.length
                            )
                          : 0}
                        %
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        OVERALL SUCCESS RATE
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Unique Functions Called */}
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <TrendingUpIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {topFunctions.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        UNIQUE FUNCTIONS USED
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Insights */}
        {!loading && (topFunctions.length > 0 || slowestFunctions.length > 0) && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Performance Insights" />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Fastest Function */}
                  {slowestFunctions.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, border: 1, borderColor: 'success.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                          ⚡ Fastest Function
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {slowestFunctions[slowestFunctions.length - 1]?.functionName}()
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'success.dark', mt: 0.5 }}>
                          {formatMs(slowestFunctions[slowestFunctions.length - 1]?.averageExecutionMs)} avg
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* Most Reliable */}
                  {topFunctions.length > 0 && (() => {
                    const mostReliable = [...topFunctions].sort((a, b) => b.successRate - a.successRate)[0];
                    return (
                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.main' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>
                            ✅ Most Reliable
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {mostReliable?.functionName}()
                          </Typography>
                          <Typography variant="h6" sx={{ color: 'primary.dark', mt: 0.5 }}>
                            {mostReliable?.successRate}% success
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {mostReliable?.successCount}/{mostReliable?.callCount} calls
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })()}

                  {/* Most Popular */}
                  {topFunctions.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'secondary.50', borderRadius: 1, border: 1, borderColor: 'secondary.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.dark', mb: 1 }}>
                          🔥 Most Popular
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {topFunctions[0]?.functionName}()
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'secondary.dark', mt: 0.5 }}>
                          {topFunctions[0]?.callCount} calls
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatMs(topFunctions[0]?.averageExecutionMs)} avg execution
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

